import React, { useEffect, useRef, useState } from 'react';
import Head from '@components/atoms/head/head';
import { Trans, useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { defaultMetas } from '@constants/metas/default';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import PageAuthenticationGuard from '@components/atoms/authentication/page-authentication-guard/page-authentication-guard';
import { NextPageWithLayout } from 'next';
import extractFromContext from '@helpers/utils/extract-from-context';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Icon from '@components/atoms/icon/icon';
import { usePreferences } from '@context/preferences.context';
import DefaultLayout from '@layouts/default-layout';
import { CheckoutEnvTemplate } from '@helpers/utils/analytics';
import { getAppInitialProps } from '@helpers/initial-props';
import { useAnalytics } from '@context/analytics/analytics.context';
import useUser from '@hooks/user/use-user';
import usePaymentOrderData from '@hooks/order-confirmation/use-payment-order-data';
import logger from '@helpers/utils/logger/client';
import { useBucket } from '@context/bucket.context';
import { LDState } from '@components/bucketTest/types';
import styles from './index.module.scss';

const OrderConfirmationPage: NextPageWithLayout = () => {
  const { updateDataLayer, scriptsEventData } = useAnalyticEvents('checkout');
  const router = useRouter();
  const { t } = useTranslation('order-confirmation');
  const { language } = usePreferences();
  const { sendEvent } = useAnalytics();
  const { userId } = useUser();
  const { isFeatureEnabled, state: ldStatus } = useBucket();
  const pageViewSentRef = useRef(false);
  const [isDatalayerUpdated, setIsDatalayerUpdated] = useState(false);

  const pageMetas = defaultMetas[language];
  const isPaymentV2 = router.query?.v === '2';

  // Use custom hook to load V2 order data from cookies/sessionStorage
  // See README.md for detailed explanation of cookie → sessionStorage pattern
  const v2OrderData = usePaymentOrderData(userId);

  // Payment V1: Read from URL query params (existing logic)
  // Initialize with empty strings to handle V2 loading state (before cookie data loads)
  let orderId = '';
  let amount = '';
  let orderAmountAtiWithSf = '';
  let orderAmountAtiWithoutSf = '';
  let pageUrl = '';
  let basketTotalValue = '';

  if (isPaymentV2 && v2OrderData) {
    // Use V2 data from cookies (no transformation needed)
    orderId = v2OrderData.orderId;
    amount = v2OrderData.orderAmountFormatted;
    orderAmountAtiWithSf = v2OrderData.datalayer?.order_amount_ati_with_sf;
    orderAmountAtiWithoutSf = v2OrderData.datalayer?.order_amount_ati_without_sf;
    basketTotalValue = v2OrderData.datalayer?.basket_total_value;
    pageUrl = v2OrderData.datalayer?.pageUrl;
  } else if (!isPaymentV2) {
    // Use V1 data from query params
    try {
      orderId = router.query?.orderId?.toString();
      amount = decodeURIComponent(atob(router.query?.orderAmount?.toString()));
    } catch (e) {
      amount = '--';
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- logger module uses require() export
      logger.error(e as Error, '[checkout] [orderConfirmation::amount]', { orderId });
    }

    try {
      const dlData = JSON.parse(atob(router.query.dl.toString())) as Record<string, unknown>;
      orderAmountAtiWithSf = dlData.order_amount_ati_with_sf as string | undefined;
      orderAmountAtiWithoutSf = dlData.order_amount_ati_without_sf as string | undefined;
      basketTotalValue = dlData.basket_total_value as string | undefined;
      pageUrl = dlData.pageUrl as string | undefined;
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- logger module uses require() export
      logger.error(e as Error, '[checkout] [orderConfirmation::dl]', { orderId });
    }
  }

  useEffect(() => {
    // V1: Run once on mount (data from query params is synchronous)
    // V2: Wait for async data from cookies/sessionStorage
    if (isPaymentV2 && !v2OrderData) {
      return;
    }

    const checkoutEnvs = [CheckoutEnvTemplate.CHECKOUT, CheckoutEnvTemplate.CART, CheckoutEnvTemplate.CART_SUMMARY];

    updateDataLayer({
      pageUrl,
      env_template: 'paiement_ok',
      screen_category: 'checkout',
      screen_subcategory: 'payment_confirmation',
      screen_name: '/payment_confirmation',
      previous_screen_name: '',
      previous_screen_category: '',
      previous_screen_subcategory: '',
      order_id: orderId,
      basket_id: orderId,
      order_amount_with_taxes: basketTotalValue,
      basket_total_value: basketTotalValue,
      order_amount_ati_with_sf: orderAmountAtiWithSf,
      order_amount_ati_without_sf: orderAmountAtiWithoutSf,
      order_confirmation_checkout_flow: checkoutEnvs
        .includes(window?.tc_vars?.env_template as CheckoutEnvTemplate)
        .toString(),
    });
    setIsDatalayerUpdated(true);

    const handleBeforeUnload = () => {
      return updateDataLayer({
        env_template: '',
        order_confirmation_checkout_flow: false.toString(),
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- For V1: empty deps like develop. For V2: only v2OrderData matters
  }, [isPaymentV2, v2OrderData]);

  // Feature flag to control deferred pageview (applies to both V1 and V2)
  // TagCommander's automatic pageview can only be disabled globally, not per feature flag
  const isLdReady = ldStatus === LDState.SUCCESS;
  const isDeferredPageviewEnabled = isLdReady
    ? isFeatureEnabled((f) => f.enableDeferredPageviewOrderConfirmation, false)
    : false;

  // Manually trigger pageview after datalayer is populated and scripts are loaded
  useEffect(() => {
    if (!isDeferredPageviewEnabled) {
      return;
    }

    const doSendPageViewEvent =
      !pageViewSentRef.current &&
      isDatalayerUpdated &&
      scriptsEventData?.isTcEvents7Loaded &&
      !!window.trackSnowplowPageView;

    if (doSendPageViewEvent) {
      pageViewSentRef.current = true;
      window.trackSnowplowPageView();
    }
  }, [isDeferredPageviewEnabled, isDatalayerUpdated, scriptsEventData?.isTcEvents7Loaded]);

  const timelineOrderLink = `/my-account/orders-sales/?orderId=${orderId}`;
  const onLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, isOrderText?: boolean) => {
    e.preventDefault();
    sendEvent({
      type: 'event',
      payload: {
        event: isOrderText ? 'checkout_click_orders' : 'checkout_click_order_reference_number',
        action: 'payment_confirmation',
        label: isOrderText ? 'click_orders' : 'click_order_reference_number',
      },
    });

    // redirect to order timeline
    window.location.href = timelineOrderLink;
  };

  return (
    <>
      <Head metadata={pageMetas} />
      <PageAuthenticationGuard redirectionUrl="/">
        <main className={styles.orderConfirmationPage}>
          <div className={styles.content}>
            <h1 className={styles.content__title}>{t('ORDER.CONFIRMATION.TITLE_CONFIRM')}</h1>
            <div className={styles.content__text}>
              <span className={styles.content__text}>
                <Trans
                  i18nKey="ORDER.CONFIRMATION.CONTENT"
                  values={{ amount }}
                  ns="order-confirmation"
                  components={{
                    orderId: (
                      <a
                        href={timelineOrderLink}
                        data-order-id={orderId}
                        onClick={(e) => onLinkClick(e)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {orderId}
                      </a>
                    ),
                  }}
                />
              </span>
              <div className={styles['separate-line']} />
              <span className={styles.content__text}>
                <Trans
                  i18nKey="ORDER.CONFIRMATION.TRACK_ORDER_STATUS"
                  ns="order-confirmation"
                  components={{
                    orderLink: (
                      <a
                        href={timelineOrderLink}
                        onClick={(e) => onLinkClick(e, true)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        .
                      </a>
                    ),
                  }}
                />
              </span>

              <div className={styles['separate-line']} />
            </div>
          </div>
          <div className={styles.fashionActivist}>
            <Icon
              className={styles.fashionActivist__icon}
              name="badge-fashion-activist"
              ariaHidden
            />
            <p className={styles.fashionActivist__text}>{t('ORDER.CONFIRMATION.FASHION_ACTIVIST.SENTENCE1')}</p>
            <a
              href="/journal/fashion-activist-badge/"
              className={styles.fashionActivist__cta}
            >
              {t('ORDER.CONFIRMATION.FASHION_ACTIVIST.CTA')}
            </a>
          </div>
        </main>
      </PageAuthenticationGuard>
    </>
  );
};

OrderConfirmationPage.Layout = [DefaultLayout];
OrderConfirmationPage.PageType = 'OrderConfirmationPage';

export async function getServerSideProps(context) {
  const { locale } = extractFromContext(context);
  const initialProps = await getAppInitialProps(context);
  const translations = await serverSideTranslations(locale, [
    'default-layout',
    'vc-authentication',
    'order-confirmation',
    'common',
    'a11y',
  ]);

  return {
    props: {
      ...initialProps,
      ...translations,
    },
  };
}

export default OrderConfirmationPage;
